<script lang="ts">
  // One cell of an overview strip: what is being counted, the count, and one
  // line saying whether that count is fine. A bare number ("4") answers
  // nothing on its own — the hint is what turns it into something you can act
  // on, so it lives in the card rather than in a tooltip nobody opens.
  //
  // Related but not the same as StatCard: that one is a borderless cell of
  // the hairline-gridded 4-up strip (StatRow). This one is a standalone
  // bordered card that can carry status, a hint and a click target.
  import type { Snippet } from 'svelte';
  import StatusLamp from './StatusLamp.svelte';

  type Tone = 'good' | 'warn' | 'bad' | 'idle';

  const {
    label,
    value,
    unit,
    hint,
    tone,
    pulse = false,
    delta,
    onClick,
    children
  }: {
    label: string;
    value: string | number;
    unit?: string;
    /** One line of context under the number. */
    hint?: string;
    /** Lights a status lamp beside the label — only where the number really
        does have a good/bad reading. */
    tone?: Tone;
    pulse?: boolean;
    /** Movement against the previous period, pre-formatted by the caller
        (the direction drives the colour, not the sign of a raw number). */
    delta?: { label: string; direction: 'up' | 'down' | 'flat' };
    onClick?: () => void;
    children?: Snippet;
  } = $props();

  const DELTA_GLYPH = { up: '↑', down: '↓', flat: '→' } as const;
</script>

<!-- Two branches rather than one <svelte:element>: a card that does nothing
     when tapped shouldn't announce itself as a button. -->
{#if onClick}
  <button type="button" class="tym-insight-card is-clickable" onclick={onClick}>
    {@render body()}
  </button>
{:else}
  <div class="tym-insight-card">{@render body()}</div>
{/if}

{#snippet body()}
  <span class="tym-insight-label">
    {#if tone}<StatusLamp {tone} {pulse} />{/if}
    {label}
  </span>
  <span class="flex items-baseline gap-1.5">
    <span class="tym-stat-value tym-insight-value">{value}</span>
    {#if unit}<span class="text-[12px] text-dark-gray">{unit}</span>{/if}
    {#if delta}
      <span class="tym-insight-delta tym-insight-delta--{delta.direction}">
        <span aria-hidden="true">{DELTA_GLYPH[delta.direction]}</span>{delta.label}
      </span>
    {/if}
  </span>
  {#if hint}<span class="tym-insight-hint">{hint}</span>{/if}
  {#if children}{@render children()}{/if}
{/snippet}
